import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();

  const navLinks = [
    { labelKey: 'nav_home', href: '/' },
    { labelKey: 'nav_products', href: '/products' },
    { labelKey: 'nav_about', href: '/about' },
    { labelKey: 'nav_contact', href: '/contact' },
  ];

  const productKeys = [
    'footer_product1',
    'footer_product2',
    'footer_product3',
    'footer_product4',
    'footer_product5',
  ];

  const contactItems = [
    { icon: 'ri-map-pin-line', textKey: 'footer_address' },
    { icon: 'ri-whatsapp-line', textKey: 'footer_whatsapp', href: 'https://wa.me/5491168139526', isLink: true },
    { icon: 'ri-mail-line', textKey: 'footer_email', href: 'mailto:cs@koorpet.com', isLink: true },
    { icon: 'ri-time-line', textKey: 'footer_hours' },
  ];

  const socialLinks = [
    { icon: 'ri-weibo-line', href: '#' },
    { icon: 'ri-whatsapp-line', href: 'https://wa.me/5491168139526' },
    { icon: 'ri-tiktok-line', href: '#' },
  ];

  return (
    <footer className="bg-[#2a1a0e] text-white px-6 md:px-10 pt-16 pb-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <span className="text-xl font-black tracking-tight text-white">Koorpet</span>
            </div>
            <p className="text-white/50 text-sm leading-relaxed mb-6">
              {t('footer_brand_desc')}
            </p>
            <div className="flex gap-3">
              {socialLinks.map((link) => (
                <a
                  key={link.icon}
                  href={link.href}
                  target={link.href.startsWith('http') ? '_blank' : undefined}
                  rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#C8714A] transition-colors cursor-pointer"
                >
                  <i className={`${link.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-sm mb-5 text-white/90">{t('footer_nav_title')}</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-white/50 text-sm hover:text-white transition-colors cursor-pointer"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-semibold text-sm mb-5 text-white/90">{t('footer_products_title')}</h4>
            <ul className="space-y-3">
              {productKeys.map((key) => (
                <li key={key}>
                  <Link
                    to="/products"
                    className="text-white/50 text-sm hover:text-white transition-colors cursor-pointer"
                  >
                    {t(key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-sm mb-5 text-white/90">{t('footer_contact_title')}</h4>
            <ul className="space-y-3">
              {contactItems.map((item) => (
                <li key={item.textKey} className="flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-[#C8714A] text-sm`}></i>
                  </div>
                  {item.isLink && item.href ? (
                    <a
                      href={item.href}
                      target={item.href.startsWith('http') ? '_blank' : undefined}
                      rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="text-white/50 text-sm hover:text-white transition-colors cursor-pointer"
                    >
                      {t(item.textKey)}
                    </a>
                  ) : (
                    <span className="text-white/50 text-sm">{t(item.textKey)}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs">
            {t('footer_copyright')}
          </p>
          <div className="flex gap-6">
            <Link to="/legal?tab=privacy" className="text-white/30 text-xs cursor-pointer hover:text-white/60 transition-colors whitespace-nowrap">{t('footer_privacy')}</Link>
            <Link to="/legal?tab=terms" className="text-white/30 text-xs cursor-pointer hover:text-white/60 transition-colors whitespace-nowrap">{t('footer_terms')}</Link>
            <Link to="/admin/login" className="text-white/20 text-xs cursor-pointer hover:text-white/40 transition-colors">
              {t('footer_admin')}
            </Link>
          </div>
        </div>

        {/* Brand Watermark */}
        <div className="mt-8 overflow-hidden">
          <div className="text-[#ffffff08] text-7xl md:text-9xl font-black tracking-widest whitespace-nowrap select-none">
            Koorpet
          </div>
        </div>
      </div>
    </footer>
  );
}
